import { useParams } from "react-router";
import { deleteHttp, post, put } from "../common/api/apiShared";
import { ProcessingActivityModel, ProcessingActivityUpdateModel, ProcessingActivityValidationViewModel, StepTypeEnum } from "./ProcessingActivity.types";
import { useProcessingActivitySteps } from "./hooks/useProcessingActivitySteps";
import { useTranslation } from "../localization/useTranslation";
import { useValidateMasterDataStep, useValidationDataSubjectStep } from "./masterDataStep/MasterDataStep.hooks";
import { useValidateDataSharingStep } from "./sharingOfDataStep/SharingOfDataStep.hooks";
import { useValidateDataCategoriesStep } from "./DataCategoriesStep/DataCategoriesStep.hooks";
import { useValidateLegalBasesStep } from "./legalBasesStep/LegalBasesStep.hooks";
import { useSelectableDataCategoriesQuery } from "./hooks/useSelectableDataCategoriesQuery";
import { DataPrivacyClassification } from "./DataCategoriesStep/DataCategoriesStep.types";
import { useValidateSystemStep } from "./systemsStep/SystemStep.hooks";
import { useOptimisticUpdate } from "../common/hooks/useOptimisticUpdate";
import { useValidateDataProcessorsStep } from "./sharingOfDataStep/dataProcessorsStep/DataProcessorsStep.hooks";
import { useValidateSourceStep } from "./sharingOfDataStep/sourceStep/SourceStep.hooks";
import { StepModel } from "../common/components/dotLegalStepper/DotLegalCoreStepper.types";
import { useResponsibleQuery } from "../user/hooks/useResponsibleQuery";
import { useValidationAssociationStep } from "./associationStep/AssociationStep.hooks";
import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useSelectableLegalEntityQuery } from "../legalEntity/useSelectableLegalEntityQuery";
import { useLegalBasesQuery } from "./hooks/useLegalBasesQuery";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { HelpTextViewModel } from "../masterData/helpText/helpTexts/HelpTexts.types";
import { useValidateJointDataControllerStep } from "./sharingOfDataStep/jointDataControllerStep/JointDataControllerStep.hooks";
import { usePlanContext } from "../auth/planProvider/PlanProvider";
import useCountryHook from "../common/hooks/useCountryList";
import { ProcessingActivityStatus } from "./processingActivities/ProcessingActivities.types";
import { ProcessingActivityStatusManagement } from "../auth/userContextProvider/UserContextProvider.types";
import { useValidateDisclosuresStep } from "./sharingOfDataStep/disclosureStep/DisclosureStep.hooks.ts";
import { useDotLegalQuery, useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export function useProcessingActivityHook(processingActivityValidation: boolean) {
    const { id } = useParams<{ id: string }>();
    const optimisticUpdate = useOptimisticUpdate();
    const snackbar = useDotLegalSnackbar();
    const { permissions } = useUserContext();
    const { dataProtectionFeatures } = usePlanContext();

    const [expanded, setExpanded] = useState(true);
    const [completeValidationDialog, setCompleteValidationDialog] = useState(false);
    const [showProcessingActivityReportDialog, setShowProcessingActivityReportDialog] = useState(false);

    const { translateString } = useTranslation();
    const validateMasterDataStep = useValidateMasterDataStep(permissions.processingActivityMasterDataPermissions?.hasPurposePermission);
    const validateSubjectStep = useValidationDataSubjectStep();
    const validateAssociationStep = useValidationAssociationStep(
        permissions.processingActivityAssociationPermissions?.hasGroupEntitiesPermission,
        permissions.processingActivityAssociationPermissions?.hasOwnershipPermission
    );
    const validateDataSharingStep = useValidateDataSharingStep(permissions, dataProtectionFeatures.registrationOfDataController);
    const validateDataCategoriesStep = useValidateDataCategoriesStep();
    const validateLegalBasesStep = useValidateLegalBasesStep(
        permissions.processingActivityLegalBasesPermissions?.hasDeletionPeriodPermission,
        permissions.processingActivityLegalBasesPermissions?.hasLegalBasisPermission
    );
    const validateSystemStep = useValidateSystemStep();

    let legalEntitiesQuery = useSelectableLegalEntityQuery();
    let dataCategoriesQuery = useSelectableDataCategoriesQuery(permissions.processingActivityDisclosuresPermissions?.hasBasisOfDisclosurePermission === true);
    let responsibleQuery = useResponsibleQuery(
        (permissions.processingActivityAssociationPermissions?.permissions.read ||
            permissions.processingActivityDisclosuresPermissions?.hasResponsiblePermission ||
            permissions.processingActivityDataProcessorsPermissions?.hasResponsiblePermission ||
            permissions.processingActivityDataControllersPermissions?.hasResponsiblePermission ||
            permissions.processingActivitySourcesPermissions?.hasResponsiblePermission) === true
    );

    const { thirdCountries } = useCountryHook();
    const legalEntityData = legalEntitiesQuery.data;

    const { legalObligationId } = useLegalBasesQuery();
    const classificationDataCategoryIds = dataCategoriesQuery.allDataCategories
        ?.filter((d) => d.classification === DataPrivacyClassification.Sensitive)
        .map((d) => d.id);

    const url = "/helptexts";
    const helpTextQuery = useDotLegalQuery<Array<HelpTextViewModel>>(url);
    let helpTexts = helpTextQuery.data ?? [];

    let queryKey = "processingActivity" + id;
    let query = useDotLegalQueryWithKey<ProcessingActivityModel>(queryKey, "/processingactivity/" + id);

    let validationData: ProcessingActivityValidationViewModel | undefined = undefined;
    let validationQuery = useDotLegalQueryWithKey<ProcessingActivityValidationViewModel>(queryKey + "validation", "/ProcessingActivityValidations/" + id, {
        enabled: processingActivityValidation,
    });

    if (processingActivityValidation && validationQuery.data) {
        validationData = {
            id: validationQuery.data.id,
            validatedSteps: validationQuery.data.validatedSteps,
            isAllStepsValid: validationQuery.data.isAllStepsValid,
        };
    }

    const defaultSteps = getDefaultSteps();
    const steps = useProcessingActivitySteps(query.data, defaultSteps, "step", validationData, helpTexts);

    const validateDataProcessorsStep = useValidateDataProcessorsStep(permissions, dataProtectionFeatures.registrationOfDataController);
    const validateDisclosuresStep = useValidateDisclosuresStep(permissions.processingActivityDisclosuresPermissions);
    const validateSourceStep = useValidateSourceStep(permissions.processingActivitySourcesPermissions);
    const validateJointDataControllerStep = useValidateJointDataControllerStep(permissions.processingActivityJointDataControllerPermissions);

    const defaultDataSharingSteps = getSharingSteps();

    const subSteps = useProcessingActivitySteps(query.data, defaultDataSharingSteps, "substep", validationData);

    const addLegalEntitiesMutation = useDotLegalMutation(addLegalEntities);

    const removeLegalEntitiesMutation = useDotLegalMutation(removeLegalEntities);

    const validateStepMutation = useDotLegalMutation(updateValidation);
    const completeProcessingActivityValidationMutation = useDotLegalMutation(completeProcessingActivityValidation);

    async function onProcessingActivityChange(processingActivity: ProcessingActivityModel) {
        // THE 2021-08-11 Her bruges ikke en mutation fra react-query, da sådan en trigger en re-render og dermed overskriver
        // fx input text i en selectbox ved auto save
        await optimisticUpdate.updateWithProcessingActivityResponse(processingActivity, saveProcessingActivity, processingActivity);
    }

    async function onSaveProcessingActivity() {
        await saveProcessingActivity(query.data!);
    }

    async function onLegalEntityChange(legalEntities: Array<string>) {
        const newProcessingActivityModel = { ...query.data! };

        const added = legalEntities.filter((l) => !newProcessingActivityModel.legalEntities.includes(l));
        const removed = newProcessingActivityModel.legalEntities.filter((val) => !legalEntities.includes(val));

        newProcessingActivityModel.legalEntities = legalEntities;

        if (added.length > 0) {
            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addLegalEntitiesMutation.mutateAsync, {
                processingActivityId: query.data!.id,
                legalEntities: added,
            });
        }
        for (const removedEntry of removed) {
            // Must not be awaited. We want to add all requests to the queue before executing any of the requests
            optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, removeLegalEntitiesMutation.mutateAsync, {
                processingActivityId: query.data!.id,
                legalEntityId: removedEntry,
            });
        }
    }

    function changeStep(step: StepTypeEnum) {
        steps.changeStep(step);
    }

    async function validateStep(step: StepTypeEnum, isValidated: boolean, currentStepIndex: number) {
        const newValidationViewModel = { ...validationQuery.data! };

        if (isValidated) {
            newValidationViewModel.validatedSteps.push(step);
        } else {
            newValidationViewModel.validatedSteps = newValidationViewModel.validatedSteps.filter((val) => val !== step);
        }

        const allStepsValid = await optimisticUpdate.updateWithProcessingActivityValidationReponse(
            newValidationViewModel,
            id,
            validateStepMutation.mutateAsync,
            {
                step: step,
                processingActivity: id,
                isValidated: isValidated,
            }
        );

        await validationQuery.refetch();

        setCompleteValidationDialog(allStepsValid);

        if (!allStepsValid) {
            const tempsteps = steps.getSteps();

            if (isValidated && currentStepIndex !== tempsteps.length - 1) {
                changeStep(tempsteps[currentStepIndex + 1].stepType);
            }
        }
    }

    async function completeValidation(processingActivityName: string) {
        await completeProcessingActivityValidationMutation.mutateAsync(id, {
            onSuccess: () => {
                snackbar.show(createElement(Trans, { i18nKey: "processingActivityValidated", values: { name: processingActivityName } }));
            },
        });
    }

    const handleCollapseClick = () => {
        if (expanded) setExpanded(false);
        else setExpanded(true);
    };

    function getDefaultSteps() {
        const defaultSteps: Array<StepModel> = [];

        if (permissions.processingActivityMasterDataPermissions) {
            defaultSteps.push(new StepModel(translateString("processingactivtiyHeader"), StepTypeEnum.masterData, validateMasterDataStep));
        }

        if (permissions.processingActivityDataSubjectPermissions) {
            defaultSteps.push(new StepModel(translateString("dataSubjectHeader"), StepTypeEnum.dataSubject, validateSubjectStep));
        }

        if (permissions.processingActivityAssociationPermissions && permissions.processingActivityAssociationPermissions.hasAnySubPermissionAccess) {
            defaultSteps.push(
                new StepModel(translateString("affiliationHeader"), StepTypeEnum.association, (model) =>
                    validateAssociationStep(model, responsibleQuery.userData)
                )
            );
        }

        if (permissions.processingActivityDataCategoryPermissions) {
            defaultSteps.push(new StepModel(translateString("dataCategoryHeader"), StepTypeEnum.dataCategories, validateDataCategoriesStep));
        }

        if (
            permissions.processingActivityLegalBasesPermissions &&
            (permissions.processingActivityLegalBasesPermissions.hasDeletionPeriodPermission ||
                permissions.processingActivityLegalBasesPermissions.hasLegalBasisPermission)
        ) {
            defaultSteps.push(
                new StepModel(translateString("legalBasisHeader"), StepTypeEnum.legalBases, (model) =>
                    validateLegalBasesStep(model, classificationDataCategoryIds, legalObligationId)
                )
            );
        }

        if (permissions.processingActivitySystemPermissions)
            defaultSteps.push(new StepModel(translateString("systemsHeader"), StepTypeEnum.systems, validateSystemStep));

        if (
            permissions.processingActivityDataProcessorsPermissions ||
            permissions.processingActivityDisclosuresPermissions ||
            permissions.processingActivitySourcesPermissions ||
            permissions.processingActivityDataControllersPermissions
        )
            defaultSteps.push(
                new StepModel(translateString("sharingOfDataStepHeader"), StepTypeEnum.sharings, (model) =>
                    validateDataSharingStep(model, legalEntityData, thirdCountries, classificationDataCategoryIds, responsibleQuery.userData, legalObligationId)
                )
            );

        if (permissions.processingActivityPolicyPermissions) {
            defaultSteps.push(new StepModel(translateString("policies"), StepTypeEnum.policies, undefined));
        }

        return defaultSteps;
    }

    function getSharingSteps() {
        let steps: Array<StepModel> = [];

        if (permissions.processingActivityDataProcessorsPermissions) {
            let numberOfDataProcessors = (query.data?.dataProcessors.filter((x) => !x.createdAsGroupEntityIsDataProcessor) ?? []).length;
            steps.push(
                new StepModel(getSubStepName(translateString("dataProcessorsStep"), numberOfDataProcessors), StepTypeEnum.dataprocessor, (model) =>
                    validateDataProcessorsStep(model, legalEntityData, thirdCountries, false, responsibleQuery.userData)
                )
            );
        }

        if (permissions.processingActivityDisclosuresPermissions) {
            let numberOfDisclosures = (query.data?.disclosures ?? []).length;
            steps.push(
                new StepModel(getSubStepName(translateString("disclosuresStep"), numberOfDisclosures), StepTypeEnum.disclosure, (model) =>
                    validateDisclosuresStep(
                        model,
                        legalEntityData,
                        dataCategoriesQuery.allDataCategories?.filter((d) => d.classification === DataPrivacyClassification.Sensitive).map((d) => d.id),
                        responsibleQuery.userData,
                        legalObligationId
                    )
                )
            );
        }

        if (permissions.processingActivityJointDataControllerPermissions) {
            let numberOfJointControllers = (query.data?.jointDataControllers ?? []).length;
            steps.push(
                new StepModel(
                    getSubStepName(translateString("jointDataControllersStep"), numberOfJointControllers),
                    StepTypeEnum.jointDataControllers,
                    (model) => validateJointDataControllerStep(model, responsibleQuery.userData)
                )
            );
        }

        if (permissions.processingActivitySourcesPermissions) {
            let numberOfSources = (query.data?.sources ?? []).length;
            steps.push(
                new StepModel(getSubStepName(translateString("sourcesStep"), numberOfSources), StepTypeEnum.sources, (model) =>
                    validateSourceStep(model, responsibleQuery.userData)
                )
            );
        }

        if (permissions.processingActivityDataControllersPermissions) {
            let numberOfDataControllers = (query.data?.dataProcessors.filter((x) => x.createdAsGroupEntityIsDataProcessor) ?? []).length;
            steps.push(
                new StepModel(getSubStepName(translateString("dataControllersStep"), numberOfDataControllers), StepTypeEnum.dataController, (model) =>
                    validateDataProcessorsStep(model, legalEntityData, thirdCountries, true, responsibleQuery.userData)
                )
            );
        }

        return steps;
    }

    function getSubStepName(name: string, numberOfSharings: number) {
        if (numberOfSharings === 0) {
            return name;
        }

        return `${name} (${numberOfSharings})`;
    }

    return {
        isLoading: query.isLoading || validationQuery.isLoading,
        processingActivity: query.data!,
        setProcessingActivity: onProcessingActivityChange,
        setProcessingActivityWithoutSaving: async (processingActivity: ProcessingActivityModel) =>
            await optimisticUpdate.setQueryData(queryKey, processingActivity),
        onSave: onSaveProcessingActivity,
        onLegalEntityChange,
        getSteps: steps.getSteps,
        getActiveStep: steps.getActiveStep,
        changeStep: changeStep,
        validationErrors: steps.validationErrors,
        subSteps,
        expanded,
        completeValidationDialog,
        setCompleteValidationDialog,
        validateStep,
        completeValidation,
        completeInProgress: completeProcessingActivityValidationMutation.isLoading,
        handleCollapseClick,
        isAllStepsValid: validationData?.isAllStepsValid,
        showProcessingActivityReportDialog,
        setShowProcessingActivityReportDialog,
    };
}

async function addLegalEntities(data: { processingActivityId: string; legalEntities: Array<string> }) {
    return await post<ProcessingActivityModel>("/processingactivity/" + data.processingActivityId + "/legalentity", { legalEntityIds: data.legalEntities });
}

async function removeLegalEntities(data: { processingActivityId: string; legalEntityId: string }) {
    return await deleteHttp<ProcessingActivityModel>("/processingactivity/" + data.processingActivityId + "/legalentity/" + data.legalEntityId);
}

async function updateValidation(data: { step: StepTypeEnum; processingActivity: string | undefined; isValidated: boolean }) {
    if (data.isValidated) {
        return await put<boolean>("/ProcessingActivityValidations/" + data.processingActivity + "/step", data.step);
    } else {
        return await deleteHttp<boolean>("/ProcessingActivityValidations/" + data.processingActivity + "/step/" + data.step);
    }
}

async function completeProcessingActivityValidation(processingActvityId: string | undefined) {
    return await put<{}>("/ProcessingActivityValidations/" + processingActvityId + "/complete", undefined);
}

async function saveProcessingActivity(viewModel: ProcessingActivityModel) {
    const updateModel: ProcessingActivityUpdateModel = {
        purposes: viewModel.purposes,
        name: viewModel.name,
        owner: viewModel.owner,
        dataSubjects: viewModel.dataSubjects,
        description: viewModel.description,
        disableDataProcessors: viewModel.disableDataProcessors,
        disableDisclosures: viewModel.disableDisclosures,
        disableSources: viewModel.disableSources,
        disableDataControllers: viewModel.disableDataControllers,
        disableJointDataControllers: viewModel.disableJointDataControllers,
        disableSystems: viewModel.disableSystems,
        policies: viewModel.policies,
        processingAreas: viewModel.processingAreas,
        id: viewModel.id,
        processors: viewModel.processors,
        securityMeasures: viewModel.securityMeasures,
        subjects: viewModel.subjects,
        sharedResponsibles: viewModel.sharedResponsibles,
        dataSources: viewModel.dataSources,
        noOfSubjects: viewModel.noOfSubjects,
        noOfProcessors: viewModel.noOfProcessors,
    };

    return await put<ProcessingActivityUpdateModel>("/processingactivity/" + viewModel.id, updateModel);
}

export function canSubmitProcessingActivityAsResearcher(
    statusManagement: ProcessingActivityStatusManagement | undefined,
    processingActivityStatus: ProcessingActivityStatus | undefined
) {
    return (
        statusManagement === ProcessingActivityStatusManagement.Draft &&
        (processingActivityStatus === ProcessingActivityStatus.Draft || processingActivityStatus === ProcessingActivityStatus.AwaitingResearcher)
    );
}

export function shouldDisableComments(isResearch: boolean, processingActivityStatus: ProcessingActivityStatus) {
    return (
        isResearch &&
        (processingActivityStatus === ProcessingActivityStatus.Active ||
            processingActivityStatus === ProcessingActivityStatus.Archived ||
            processingActivityStatus === ProcessingActivityStatus.Cancelled)
    );
}
